import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { formatRelativeTime } from "@/lib/utils";
import {
  useProductChanges,
  useVariantChanges,
  useSetProductReviewStatus,
  useSetVariantReviewStatus,
} from "@/hooks/useReview";
import { useAuth } from "@/hooks/useAuth";
import type {
  ReviewQueueItem,
  ChangeSummary,
  ReviewStatus,
} from "@/types/review";
import * as reviewApi from "@/services/reviewApi";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ReviewItemDialogProps {
  item: ReviewQueueItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === "reviewed"
      ? "default"
      : status === "stale"
        ? "destructive"
        : status === "canceled"
          ? "outline"
          : "secondary";
  const colorClass =
    status === "reviewed"
      ? "bg-[#10B981] border-[#10B981] text-white"
      : status === "stale"
        ? "bg-[#F59E0B] border-[#F59E0B] text-white"
        : status === "in_progress"
          ? "bg-[#3B82F6] border-[#3B82F6] text-white"
          : status === "canceled"
            ? "bg-[#94A3B8] border-[#94A3B8] text-white"
            : "bg-[#6B7280] border-[#6B7280] text-white";
  return (
    <Badge variant={variant} className={cn("uppercase", colorClass)}>
      {status}
    </Badge>
  );
}

const REVIEW_STATUS_OPTIONS: { value: ReviewStatus; label: string }[] = [
  { value: "reviewed", label: "Reviewed" },
  { value: "in_progress", label: "In Progress" },
  { value: "canceled", label: "Canceled" },
  { value: "stale", label: "Stale" },
  { value: "unreviewed", label: "Unreviewed" },
];

function getStatusButtonLabel(status: ReviewStatus): string {
  const opt = REVIEW_STATUS_OPTIONS.find((o) => o.value === status);
  return opt ? `Mark as ${opt.label}` : `Mark as ${status}`;
}

/** Recursively sort object keys so JSON output is comparable (same key order in old vs new). */
function sortObjectKeys<T>(value: T): T {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(sortObjectKeys) as T;
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(value as object).sort()) {
    sorted[key] = sortObjectKeys((value as Record<string, unknown>)[key]);
  }
  return sorted as T;
}

function formatChangeValue(
  v: string | number | boolean | null | object | unknown[],
): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") {
    try {
      return JSON.stringify(sortObjectKeys(v), null, 2);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function ChangesBlock({
  summary,
  title,
}: {
  summary: ChangeSummary | null;
  title?: string;
}) {
  const header =
    title ??
    (summary
      ? `Changes since last review (${formatRelativeTime(summary.last_reviewed_at)} by ${summary.reviewed_by})`
      : "Changes since last review");
  return (
    <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-4 space-y-3">
      <p className="text-sm font-medium text-muted-foreground">{header}</p>
      {summary?.changes?.length ? (
        <div className="space-y-3">
          {summary.changes.map((c, i) => (
            <div key={i} className="text-sm space-y-1">
              <span className="font-medium text-foreground">{c.field}</span>
              <div className="grid grid-cols-2 gap-2 mt-1 min-w-0">
                <div className="min-w-0 text-red-600 dark:text-red-400">
                  <span className="text-muted-foreground shrink-0">Old: </span>
                  <pre className="mt-0.5 text-xs wrap-break-word whitespace-pre-wrap max-h-48 overflow-auto rounded bg-muted/50 p-2 font-sans">
                    {formatChangeValue(c.old_value)}
                  </pre>
                </div>
                <div className="min-w-0 text-green-600 dark:text-green-400">
                  <span className="text-muted-foreground shrink-0">New: </span>
                  <pre className="mt-0.5 text-xs wrap-break-word whitespace-pre-wrap max-h-48 overflow-auto rounded bg-muted/50 p-2 font-sans">
                    {formatChangeValue(c.new_value)}
                  </pre>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No field-level changes reported.
        </p>
      )}
    </div>
  );
}

export function ReviewItemDialog({
  item,
  open,
  onOpenChange,
  onSuccess,
}: ReviewItemDialogProps) {
  const [notes, setNotes] = useState("");
  const [selectedStatus, setSelectedStatus] =
    useState<ReviewStatus>("reviewed");
  const [bulkResolving, setBulkResolving] = useState(false);
  const { user } = useAuth();
  const reviewerId =
    user?.email ?? user?.name ?? (user ? String(user.id) : "unknown");

  const isProduct = item?.entity_type === "product";
  const needChanges = item?.review_status === "stale";

  const {
    data: productChanges,
    isLoading: loadingProductChanges,
    isError: productChangesError,
    refetch: refetchProductChanges,
  } = useProductChanges(
    item?.product_id ?? 0,
    open && !!item && isProduct && needChanges,
  );
  const {
    data: variantChanges,
    isLoading: loadingVariantChanges,
    isError: variantChangesError,
    refetch: refetchVariantChanges,
  } = useVariantChanges(
    item?.variant_id ?? 0,
    open && !!item && !isProduct && needChanges,
  );

  const setProductStatus = useSetProductReviewStatus();
  const setVariantStatus = useSetVariantReviewStatus();

  const changes: ChangeSummary | null =
    (isProduct ? productChanges : variantChanges) ?? null;
  const loadingChanges =
    needChanges && (loadingProductChanges || loadingVariantChanges);
  const changesError =
    needChanges && (productChangesError || variantChangesError);
  const refetchChanges = isProduct
    ? refetchProductChanges
    : refetchVariantChanges;

  const changedFieldsForBulk = useMemo(() => {
    if (!needChanges) return null;
    if (!changes || !Array.isArray(changes.changes)) return [];
    const fields = Array.from(
      new Set(changes.changes.map((c) => c.field).filter(Boolean)),
    );
    fields.sort();
    return fields;
  }, [needChanges, changes]);

  // Reset on close; when opening, pre-fill notes from existing review data
  useEffect(() => {
    if (!open) {
      setNotes("");
      setSelectedStatus("reviewed");
      return;
    }
    if (item) setNotes(item.notes ?? "");
  }, [open, item?.notes]);

  // When changes load for a stale item, use their notes if queue didn't include notes
  useEffect(() => {
    if (!open || !needChanges || !changes?.notes || item?.notes) return;
    setNotes((prev) => (prev === "" ? (changes.notes ?? "") : prev));
  }, [open, needChanges, changes?.notes, item?.notes]);

  const existingNotes =
    item?.notes?.trim() || changes?.notes?.trim() || "" || null;

  if (!item) return null;

  const handleBulkResolveSimilar = async () => {
    if (!item || !needChanges || !changedFieldsForBulk) return;
    setBulkResolving(true);
    try {
      const body = {
        entity_type: item.entity_type,
        changed_fields: changedFieldsForBulk,
        reviewer_type: "human" as const,
        reviewer_identifier: reviewerId,
        // Let backend apply its default notes when none are provided.
      };
      const res = await reviewApi.bulkResolveStaleGroup(item.source, body);
      toast.success(
        `Bulk-resolved ${res.resolved_count} ${res.entity_type} review(s) for ` +
          (changedFieldsForBulk.length > 0
            ? `changes to [${changedFieldsForBulk.join(", ")}]`
            : "pre-tracking stale reviews"),
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : "Failed to bulk-resolve related stale reviews",
      );
    } finally {
      setBulkResolving(false);
    }
  };

  const handleSubmit = async () => {
    const body = {
      status: selectedStatus,
      reviewer_type: "human" as const,
      reviewer_identifier: reviewerId,
      review_type: "direct" as const,
      ...(notes.trim() && { notes: notes.trim() }),
    };
    try {
      if (isProduct) {
        await setProductStatus.mutateAsync({
          productId: item.product_id,
          body,
        });
        toast.success(
          `Product marked as ${REVIEW_STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.label ?? selectedStatus}`,
        );
      } else {
        await setVariantStatus.mutateAsync({
          variantId: item.variant_id!,
          body,
        });
        toast.success(
          `Variant marked as ${REVIEW_STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.label ?? selectedStatus}`,
        );
      }
      setNotes("");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : `Failed to mark as ${selectedStatus}`,
      );
    }
  };

  const isPending = setProductStatus.isPending || setVariantStatus.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Review: {item.product_name}
            {item.variant_name && (
              <span className="text-muted-foreground font-normal">
                — {item.variant_name}
              </span>
            )}
            <StatusBadge status={item.review_status} />
            <Badge variant="outline">{item.entity_type}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-muted-foreground">Source</Label>
              <p className="mt-1">{item.source}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Last reviewed</Label>
              <p className="mt-1">
                {item.last_reviewed_at
                  ? formatRelativeTime(item.last_reviewed_at)
                  : "Never"}
              </p>
            </div>
          </div>

          {existingNotes && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                Existing notes from last review
              </Label>
              <p className="mt-1.5 text-sm whitespace-pre-wrap">
                {existingNotes}
              </p>
            </div>
          )}

          {needChanges && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-4 space-y-3">
              <p className="text-sm font-medium text-muted-foreground">
                Changes since last review
              </p>
              {loadingChanges ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading diff…
                </div>
              ) : changesError ? (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-destructive">
                    Could not load changes.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => refetchChanges()}
                  >
                    Retry
                  </Button>
                </div>
              ) : changes ? (
                <ChangesBlock
                  summary={changes}
                  title={`${formatRelativeTime(changes.last_reviewed_at)} by ${changes.reviewed_by}`}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No field-level changes reported.
                </p>
              )}
              {changedFieldsForBulk && !changesError && (
                <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-amber-500/30 mt-3">
                  <p className="text-xs text-muted-foreground">
                    {changedFieldsForBulk.length > 0
                      ? `You can bulk-resolve all stale ${item?.entity_type ?? "entity"} reviews for this source with the same changed fields.`
                      : "You can bulk-resolve all stale reviews for this source that went stale before change tracking was enabled."}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={bulkResolving || loadingChanges}
                    onClick={handleBulkResolveSimilar}
                  >
                    {bulkResolving && (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    )}
                    Mark all related stale as reviewed
                  </Button>
                </div>
              )}
            </div>
          )}

          <div>
            <Label>Review status</Label>
            <Select
              value={selectedStatus}
              onValueChange={(v) => setSelectedStatus(v as ReviewStatus)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REVIEW_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="review-notes">Notes (optional)</Label>
            <Textarea
              id="review-notes"
              className="mt-1"
              placeholder="e.g. Verified against source page"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {getStatusButtonLabel(selectedStatus)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
